import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { InFlight } from '../http/in-flight';
import { HTTPCONSTANTS } from '../http/http-constants';

/** Rail/tile counts for the account overview. */
export interface AccountSummary {
  followedPlates: number;
  /** Saved plates (personal shortlist). */
  savedPlates?: number;
  savedSearches: number;
  registrations: number;
  recentlyViewed: number;
  newUpdates: number;
}

export interface LatestEvent {
  eventType: string;
  pricePence: number | null;
  previousPricePence: number | null;
  occurredAt: string | null;
  detectedAt: string;
  provisional: boolean;
  payload: string | null;
}

export interface FollowedPlate {
  id: number;
  registration: string;
  /** Canonical DVLA-spaced display form (backend IPlateService). */
  formattedRegistration?: string;
  intent: 'considering' | 'tracking' | 'notify' | null;
  alertTypes: string[]; // available | price | new_listings | sale_result; [] = bookmark
  frequency: 'instant' | 'daily' | 'weekly' | 'none';
  followedAt: string;
  latestEvent: LatestEvent | null;
}

/** Saved plate: the personal shortlist (no alerts). */
export interface SavedPlate {
  id: number;
  registration: string;
  formattedRegistration?: string;
  /** The search the plate was saved from, when recorded. */
  term: string | null;
  /** Null for plates saved before saved dates were recorded. */
  savedAt: string | null;
  latestEvent: LatestEvent | null;
}

export interface MergeSavedResult {
  added: number;
  alreadySaved: number;
  invalid: number;
  saved: SavedPlate[];
}

export interface UserRegistrationRequest {
  registration?: string;
  ownershipStatus?: string;
  statusExpiryDate?: string | null;
  purchasePricePence?: number | null;
  purchaseDate?: string | null;
  purchaseSource?: string | null;
  sellIntent?: string;
  vehicleMake?: string | null;
  vehicleModel?: string | null;
  vehicleYear?: number | null;
}

export interface UserRegistrationView {
  id: number;
  registration: string;
  ownershipStatus: 'assigned' | 'v750' | 'v778' | 'in_transfer' | 'unsure';
  statusExpiryDate: string | null;
  purchasePricePence: number | null;
  purchaseDate: string | null;
  purchaseSource: string | null;
  sellIntent: 'no' | 'maybe' | 'yes';
  createdAt: string;
  vehicleMake: string | null;
  vehicleModel: string | null;
  vehicleYear: number | null;
  lastKnownSale: {
    pricePence: number;
    soldDate: string;
    saleTypeLabel: string;
    basis: string;
  } | null;
}

export interface AlertActionInfo {
  purpose: string;
  description: string;
  valid: boolean;
  invalidReason: string | null;
}

export interface SavedSearch {
  id: number;
  name: string;
  criteria: string;      // structured JSON
  plainSummary: string;
  alertScope: string[];  // new | price_reductions | newly_available | auction
  frequency: 'instant' | 'daily' | 'weekly' | 'none';
  pausedAt: string | null;
  lastCheckedAt: string | null;
  lastVisitedAt: string | null;
  createdAt: string;
}

export interface SavedSearchRequest {
  name?: string;
  criteria?: string;
  plainSummary?: string;
  alertScope?: string[];
  frequency?: string;
}

/** POST /account/searches/estimate (PlatexAPI SavedSearchService.Estimate). */
export interface SavedSearchEstimate {
  plainSummary: string;
  /** False until the listing diff has a baseline. */
  available: boolean;
  /** True when term scores were sampled. */
  approximate: boolean;
  currentlyListed: number;
  newLast28Days: number;
  newPerWeek: number;
  newPerDay: number;
  instantAllowed: boolean;
  suggestedFrequency: string;
  sample: SavedSearchPlate[];
}

export interface SavedSearchPlate {
  registration: string;
  allInPence: number | null;
  previousAllInPence: number | null;
  eventType: string | null;
  since: string | null;
  isNew: boolean;
}

/** GET /account/searches/{id}/matches: one row per plate, newest first. */
export interface SavedSearchMatches {
  searchId: number;
  plainSummary: string;
  lastVisitedAt: string | null;
  total: number;
  newCount: number;
  priceDrops: number;
  items: SavedSearchPlate[];
}

export interface PlateViewEvent {
  id: number;
  registration: string;
  /** Canonical DVLA-spaced display form (backend IPlateService). */
  formattedRegistration?: string;
  viewedAt: string;
}

/** Deterministic structured match reason (Phase 5; reasonVersion=1). */
export interface MatchReason {
  reasonVersion: number;
  causeType: string;
  rule: string;
  matchStrength: 'exact' | 'related';
  isExact: boolean;
  interestType?: string | null;
  value?: string | null;
  matched?: string | null;
  savedSearchId?: number | null;
  criterion?: string | null;
}

export interface ForYouCard {
  registration: string;
  /** Canonical DVLA-spaced display form (backend IPlateService). */
  formattedRegistration?: string;
  format: string | null;
  isNumberOne: boolean;
  pricePence: number | null;
  reason: MatchReason;
  reasons: MatchReason[];
  matchStrength: 'exact' | 'related';
  targetUrl: string;
  auction: {
    house: string;
    auctionCode: string;
    title: string | null;
    status: string;
    closesAt: string | null;
    closesAtConfirmed: boolean;
    daysRemaining: number | null;
  } | null;
}

export interface ForYouResponse {
  asOf: string;
  cardCount: number;
  quiet: boolean;
  cards: ForYouCard[];
}

export interface AuctionFixture {
  hasAuction: boolean;
  house: string | null;
  auctionCode: string | null;
  title: string | null;
  status: string | null;
  opensAt: string | null;
  closesAt: string | null;
  opensAtConfirmed: boolean;
  closesAtConfirmed: boolean;
  daysRemaining: number | null;
  catalogueAvailable: boolean;
  lotCount: number | null;
  matchingLotCount: number | null;
  quietReason: string | null;
}

export interface UserInterest {
  id: number;
  type: string;
  value: string;
  source: string;
  status: string;
  createdAt: string;
}

/** Server-composed My Market briefing (Phase 3; PHASE3_BRIEFING_CONTRACT.md). */
export interface BriefingResponse {
  asOf: string;
  lastSeenAt: string | null;
  summary: { newItemCount: number; quiet: boolean };
  items: BriefingItem[];
  usedEventIds: string[];
}

export interface BriefingItem {
  eventId: string | null;
  type: string; // dotted API event name
  registration: string | null;
  /** Canonical DVLA-spaced display form (backend IPlateService). */
  formattedRegistration?: string | null;
  headline: string;
  reason: {
    type: string;
    label: string;
    trace: unknown; // D2 audit trace; dev tooling only, never rendered
  };
  marketNote: { type: string; text: string } | null;
  sourceCategory: string | null;
  provisional: boolean;
  occurredAt: string | null;
  detectedAt: string | null;
  collapsed: { count: number; fromPence: number | null; toPence: number | null } | null;
  /** May carry a query string (auction sale rows: /auctions?tab=soon&sale=B287). */
  targetUrl: string;
  /** Auction sale rows: up to 3 lots from that sale, the member's matches first. */
  picks?: BriefingPick[] | null;
  /** Auction rows: the facts line shown instead of the reason and age ("5 plates · 4 sold · 1 unsold · £87,194 in winning bids"). */
  detail?: string | null;
}

export interface BriefingPick {
  lotId: number;
  registration: string;
  formattedRegistration: string | null;
  kind: string; // match | name | word | number_one | short | sold
  label: string;
  targetUrl: string;
}

export interface AccountUpdateItem {
  deliveryId: number;
  marketEventId: string;
  readState: 'new' | 'read' | 'dismissed';
  displayCauseType: 'user_registration' | 'followed_plate' | 'saved_search';
  displayCauseId: number;
  causes: { type: string; id: number }[];
  createdAt: string;
  eventType: string | null;
  registration: string | null;
  source: string | null;
  occurredAt: string | null;
  detectedAt: string | null;
  pricePence: number | null;
  previousPricePence: number | null;
  provisional: boolean;
  payload: string | null;
}

/**
 * GET /account/capabilities: what the alert system behind this API really
 * honours. The account only offers these settings (production's alert jobs
 * ignore the rest). emailAlerts is whether this member's alerts are emailed
 * at all; otherwise they arrive as PlateX messages and in Updates.
 */
export interface AccountCapabilities {
  name: string;
  plateAlertTypes: string[];
  plateFrequencies: string[];
  plateAlertsOptional: boolean;
  searchCriteria: string[];
  searchStyles: string[];
  searchTermRequired: boolean;
  searchScopes: string[];
  searchFrequencies: string[];
  /** Match lists, counts and estimates (DEV2's listing state). */
  searchMatches: boolean;
  /** "Latest change" lines and read/unread Updates. */
  marketEvents: boolean;
  emailAlerts: boolean;
}

/**
 * Account system v1 API (PlatexAPI AccountController). Every endpoint is
 * JWT-scoped server side; this service only ever sees the signed-in user's
 * own data.
 */
@Injectable({ providedIn: 'root' })
export class AccountV2Service {
  private base = `${HTTPCONSTANTS.protocol}${HTTPCONSTANTS.apiBaseAddress}/account`;

  constructor(private http: HttpClient) { }

  // Per member (emailAlerts), so never kept beyond the request itself.
  private capabilitiesInFlight = new InFlight<AccountCapabilities>();
  public getCapabilities(): Observable<AccountCapabilities> {
    return this.capabilitiesInFlight.get(() => this.http.get<AccountCapabilities>(`${this.base}/capabilities`));
  }

  public getSummary(): Observable<AccountSummary> {
    return this.http.get<AccountSummary>(`${this.base}/summary`);
  }

  // ---- Followed plates ----

  public getFollowed(): Observable<FollowedPlate[]> {
    return this.http.get<FollowedPlate[]>(`${this.base}/followed`);
  }

  public follow(registration: string, intent: string | null): Observable<FollowedPlate> {
    return this.http.post<FollowedPlate>(`${this.base}/followed`, { registration, intent });
  }

  public updateFollowAlerts(registration: string, alertTypes: string[], frequency: string): Observable<FollowedPlate> {
    return this.http.put<FollowedPlate>(
      `${this.base}/followed/${encodeURIComponent(registration)}/alerts`,
      { alertTypes, frequency }
    );
  }

  public unfollow(registration: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/followed/${encodeURIComponent(registration)}`);
  }

  // ---- Saved plates (shortlist; idempotent) ----

  public getSaved(): Observable<SavedPlate[]> {
    return this.http.get<SavedPlate[]>(`${this.base}/saved`);
  }

  public savePlate(registration: string, term?: string): Observable<SavedPlate> {
    return this.http.put<SavedPlate>(`${this.base}/saved/${encodeURIComponent(registration)}`, { term: term || null });
  }

  public unsavePlate(registration: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/saved/${encodeURIComponent(registration)}`);
  }

  public mergeSaved(registrations: string[]): Observable<MergeSavedResult> {
    return this.http.post<MergeSavedResult>(`${this.base}/saved/merge`, { registrations });
  }

  // ---- Saved searches ----

  public getSearches(): Observable<SavedSearch[]> {
    return this.http.get<SavedSearch[]>(`${this.base}/searches`);
  }

  public createSearch(request: SavedSearchRequest): Observable<SavedSearch> {
    return this.http.post<SavedSearch>(`${this.base}/searches`, request);
  }

  public updateSearch(id: number, request: SavedSearchRequest): Observable<SavedSearch> {
    return this.http.put<SavedSearch>(`${this.base}/searches/${id}`, request);
  }

  public setSearchPaused(id: number, paused: boolean): Observable<SavedSearch> {
    return this.http.put<SavedSearch>(`${this.base}/searches/${id}/paused/${paused}`, {});
  }

  public deleteSearch(id: number): Observable<void> {
    return this.http.delete<void>(`${this.base}/searches/${id}`);
  }

  public estimateSearch(criteria: string): Observable<SavedSearchEstimate> {
    return this.http.post<SavedSearchEstimate>(`${this.base}/searches/estimate`, { criteria });
  }

  public getSearchMatches(id: number, page = 1, pageSize = 50): Observable<SavedSearchMatches> {
    return this.http.get<SavedSearchMatches>(`${this.base}/searches/${id}/matches`, { params: { page, pageSize } });
  }

  public touchSearchVisited(id: number): Observable<void> {
    return this.http.post<void>(`${this.base}/searches/${id}/visited`, {});
  }

  // ---- Recently viewed ----

  public getRecent(limit: number = 50): Observable<PlateViewEvent[]> {
    return this.http.get<PlateViewEvent[]>(`${this.base}/recent?limit=${limit}`);
  }

  public recordView(registration: string): Observable<void> {
    return this.http.post<void>(`${this.base}/recent/${encodeURIComponent(registration)}`, {});
  }

  public clearRecent(): Observable<void> {
    return this.http.delete<void>(`${this.base}/recent`);
  }

  // ---- My Registrations ----

  public getRegistrations(): Observable<UserRegistrationView[]> {
    return this.http.get<UserRegistrationView[]>(`${this.base}/registrations`);
  }

  public addRegistration(request: UserRegistrationRequest): Observable<UserRegistrationView> {
    return this.http.post<UserRegistrationView>(`${this.base}/registrations`, request);
  }

  public updateRegistration(id: number, request: UserRegistrationRequest): Observable<UserRegistrationView> {
    return this.http.put<UserRegistrationView>(`${this.base}/registrations/${id}`, request);
  }

  public removeRegistration(id: number): Observable<void> {
    return this.http.delete<void>(`${this.base}/registrations/${id}`);
  }

  // ---- Tokenised alert actions (work without login) ----

  public describeAlertAction(token: string): Observable<AlertActionInfo> {
    return this.http.get<AlertActionInfo>(
      `${HTTPCONSTANTS.protocol}${HTTPCONSTANTS.apiBaseAddress}/alertactions/${encodeURIComponent(token)}`);
  }

  public consumeAlertAction(token: string): Observable<AlertActionInfo> {
    return this.http.post<AlertActionInfo>(
      `${HTTPCONSTANTS.protocol}${HTTPCONSTANTS.apiBaseAddress}/alertactions/${encodeURIComponent(token)}`, {});
  }

  // ---- Updates ----

  /** Server-composed briefing. Reading NEVER advances the anchor. */
  public getBriefing(): Observable<BriefingResponse> {
    return this.http.get<BriefingResponse>(`${this.base}/briefing`);
  }

  /** "For You" recommendations over live auction lots (deterministic reasons). */
  public getForYou(): Observable<ForYouResponse> {
    return this.http.get<ForYouResponse>(`${this.base}/for-you`);
  }

  /** Homepage auction fixture (always a valid state; matchingLotCount when authed). */
  public getAuctionFixture(): Observable<AuctionFixture> {
    return this.http.get<AuctionFixture>(
      `${HTTPCONSTANTS.protocol}${HTTPCONSTANTS.apiBaseAddress}/auction/fixture`
    );
  }

  // The homepage briefing line and For You both load interests on arrival.
  private interestsInFlight = new InFlight<UserInterest[]>();
  public getInterests(): Observable<UserInterest[]> {
    return this.interestsInFlight.get(() =>
      this.http.get<UserInterest[]>(`${this.base}/interests`)
    );
  }
  public addInterest(type: string, value: string): Observable<UserInterest> {
    return this.http.post<UserInterest>(`${this.base}/interests`, { type, value });
  }
  public removeInterest(id: number): Observable<void> {
    return this.http.delete<void>(`${this.base}/interests/${id}`);
  }

  // --- admin: minimal catalogue import (admin-gated server-side) ---
  private adminBase = `${HTTPCONSTANTS.protocol}${HTTPCONSTANTS.apiBaseAddress}/auctionadmin`;
  public previewCatalogue(request: unknown): Observable<any> {
    return this.http.post<any>(`${this.adminBase}/preview-catalogue`, request);
  }
  public importCatalogue(request: unknown): Observable<any> {
    return this.http.post<any>(`${this.adminBase}/import-catalogue`, request);
  }

  /**
   * Records the visit (advances last_seen_at, monotonic server-side).
   * Contract: call ONLY after the member briefing has rendered
   * successfully — never on prefetch, never when the GET failed.
   */
  public markBriefingSeen(): Observable<void> {
    return this.http.post<void>(`${this.base}/briefing/seen`, {});
  }

  public getUpdates(cause?: 'saved_search' | 'followed_plate' | 'user_registration'): Observable<AccountUpdateItem[]> {
    const query = cause ? `?cause=${cause}` : '';
    return this.http.get<AccountUpdateItem[]>(`${this.base}/updates${query}`);
  }

  public markUpdateRead(deliveryId: number): Observable<void> {
    return this.http.post<void>(`${this.base}/updates/${deliveryId}/read`, {});
  }

  public markAllUpdatesRead(): Observable<void> {
    return this.http.post<void>(`${this.base}/updates/read-all`, {});
  }
}
